import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useParams } from "react-router";
import { Button } from "components";
import { useThemesContext } from "../../../../../context/ThemeProvider";
import ImageItem from "./ImageItem";
import InitialImage from "./InitialImage";

const SettingsSelectImage = ({ id, label, initialValue, onChange }) => {

  const { themeId } = useParams();
  const { uploadImages, selectedTheme } = useThemesContext();

  const [image, setImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [allImages, setAllImages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const hasImage = !!image;

  const onDrop = async acceptedImages => {
    if (!!acceptedImages.length) {
      setIsOpen(true);
      setAllImages(prevImages => [
        ...prevImages,
        ...acceptedImages.map(image => ({ ...image, id: '' })),
      ]);

      const uploadedImages = await uploadImages(themeId, acceptedImages);

      if (uploadedImages && !!uploadedImages.length) {
        setAllImages(prevImages => [
          ...prevImages.slice(0, -acceptedImages.length),
          ...uploadedImages,
        ]);
      } else {
        setAllImages(prevImages => prevImages.slice(0, -acceptedImages.length));
      }
    }
  };


  const getImages = () => {
    const allowedFileExtensions = ['jpg', 'jpeg', 'png', 'gif'];

    if (!!selectedTheme?.assets) {
      const images = Object.values(selectedTheme?.assets)
        ?.filter(({ file }) =>  allowedFileExtensions.includes(file.name.split('.').pop()))
        .map(({ file }) => file);

      setAllImages(images);

      if (!!initialValue) {
        setImage(images?.find(({ cdn_url }) => cdn_url === initialValue));
      }
    }
  };

  const {
    getRootProps,
    getInputProps,
    open,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop: onDrop,
    multiple: true,
    accept: 'image/png, image/gif, image/jpg, image/jpeg',
  });

  const handleShowImages = event => {
    event.stopPropagation();

    !!allImages.length ? setIsOpen(true) : open();
  };

  const handleClose = event => {
    event.stopPropagation();

    setSelectedImage(null);
    setIsOpen(false);
  };

  const handleUploadImages = event => {
    event.stopPropagation();

    open();
  };

  const handleChange = (event, image) => {
    event.stopPropagation();

    setSelectedImage(image);
  };

  const handleSelect = event => {
    event.stopPropagation();

    setImage(selectedImage);
    onChange(id, selectedImage.cdn_url);
    setSelectedImage(null);
    setIsOpen(false);
  };

  const handleChangeImage = image => {
    setSelectedImage(image);
    setIsOpen(true);
  };

  const handleDeleteImage = () => {
    setImage(null);
    onChange(id, '');
  };

  const showInfoText = !!(isDragAccept || isDragReject);

  const renderInfoText = () => (
    <div style={{ color: isDragAccept ? 'success.light' : 'error'}} className="text-center">
      {isDragAccept ? 'Drop images to upload' : 'Image type is not valid'}
    </div>
  );
  
  const ImageIcon = () => <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 8.5H15.01" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17 4.5H7C5.34315 4.5 4 5.84315 4 7.5V17.5C4 19.1569 5.34315 20.5 7 20.5H17C18.6569 20.5 20 19.1569 20 17.5V7.5C20 5.84315 18.6569 4.5 17 4.5Z" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4 15.498L8 11.498C8.45606 11.0592 8.97339 10.8281 9.5 10.8281C10.0266 10.8281 10.5439 11.0592 11 11.498L16 16.498" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 14.498L15 13.498C15.4561 13.0592 15.9734 12.8281 16.5 12.8281C17.0266 12.8281 17.5439 13.0592 18 13.498L20 15.498" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>


  const renderSelectImage = showSelectButton => (
    <div className="w-100">
      <div className="visual-menu-images-container">
        <div className="visual-menu-image-new" style={isDragAccept ? { borderColor: "var(--green6)" } : isDragReject ? { borderColor: "var(--red6)" } : {}} onClick={e => handleUploadImages(e)}>
          {showInfoText ? (renderInfoText()) : (
            <>
              <ImageIcon />
              <div>Upload</div>
            </>
          )}
        </div>

        {allImages?.map((image, idx) => (
          <ImageItem
            key={idx}
            image={image}
            onChange={handleChange}
            selected={selectedImage?.id === image.id}
          />
        ))}
      </div>

      <div className="d-flex w-100 justify-content-end mt-2">
        <Button variant="secondary" size="large" onClick={e => handleClose(e)} className={showSelectButton ? 'w-50 mr-2' : 'w-100'}>
          Cancel
        </Button>

        {showSelectButton && <Button size="large" className="w-50" onClick={e => handleSelect(e)}>Select</Button>}
      </div>
    </div>
  );

  useEffect(() => {
    getImages();
  }, [initialValue]);

  return (
    <div className="mb-4">
      <div className="paylixecommerce-label">
        {label}
      </div>

      {hasImage ? (
        <>
          {isOpen ? (
            <div className={isFocused ? 'isFocused' : isDragAccept ? 'isDragAccept' : isDragReject ? 'isDragReject' : ''} {...getRootProps({ isFocused, isDragAccept, isDragReject })}>
              <input {...getInputProps()} />

              {renderSelectImage(image?.id !== selectedImage?.id)}
            </div>
          ) : (
            <div>
              <InitialImage
                image={image}
                onChange={handleChangeImage}
                onDelete={handleDeleteImage}
              />
            </div>
          )}
        </>
      ) : (
        <div className={isFocused ? 'isFocused' : isDragAccept ? 'isDragAccept' : isDragReject ? 'isDragReject' : ''} {...getRootProps({ isFocused, isDragAccept, isDragReject })}>
          <input {...getInputProps()} />

          {isOpen ? renderSelectImage(!!selectedImage) :
              (<>
                {showInfoText ? renderInfoText() : (
                  <Button size="large" className="w-100" onClick={e => handleShowImages(e)}>
                    Select Image
                  </Button>
                )}
              </>)
          }
        </div>
      )}
    </div>
  );
};

export default SettingsSelectImage;
